/**
 * Import Wallet Form
 *
 * Form to import an existing wallet via mnemonic, private key, or backup file
 */

import { useState } from "react";
import {
  ArrowLeft,
  Key,
  FileText,
  Loader2,
  Fingerprint,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useBrowserWallet, useBrowserWalletStore } from "@nullaone/wallet-react";

type ImportMethod = "mnemonic" | "privateKey" | "backup";

interface ImportFormState {
  method: ImportMethod;
  value: string;
  name: string;
  isImporting: boolean;
}

export function ImportWalletForm() {
  const { importWallet, restoreFromBackup, connecting, closeModal } =
    useBrowserWallet();
  const { setModalStep, setError } = useBrowserWalletStore();

  const [state, setState] = useState<ImportFormState>({
    method: "mnemonic",
    value: "",
    name: "",
    isImporting: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleImport = async () => {
    // Handle backup method separately
    if (state.method === "backup") {
      if (!selectedFile) {
        setError("Please select a backup file");
        return;
      }
      setState((prev) => ({ ...prev, isImporting: true }));
      try {
        const content = await selectedFile.text();
        await restoreFromBackup(content);
        setModalStep("select"); // Go back to select to authenticate
      } catch {
        setState((prev) => ({ ...prev, isImporting: false }));
      }
      return;
    }

    // Handle mnemonic/privateKey methods
    if (!state.value.trim()) {
      setError("Please enter your recovery phrase or private key");
      return;
    }

    setState((prev) => ({ ...prev, isImporting: true }));

    try {
      await importWallet(
        state.method,
        state.value.trim(),
        state.name || undefined,
      );
      closeModal();
    } catch {
      setState((prev) => ({ ...prev, isImporting: false }));
    }
  };

  const isDisabled =
    state.isImporting ||
    connecting ||
    (state.method === "backup" ? !selectedFile : !state.value.trim());

  return (
    <div className="space-y-6 py-4">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => setModalStep("select")}
        disabled={state.isImporting}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <div className="space-y-4">
        {/* Import method selection */}
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant={state.method === "mnemonic" ? "default" : "outline"}
            className="h-auto py-3 flex-col gap-1"
            onClick={() =>
              setState((prev) => ({ ...prev, method: "mnemonic", value: "" }))
            }
            disabled={state.isImporting}
          >
            <FileText className="h-5 w-5" />
            <span className="text-xs">Recovery Phrase</span>
          </Button>
          <Button
            variant={state.method === "privateKey" ? "default" : "outline"}
            className="h-auto py-3 flex-col gap-1"
            onClick={() =>
              setState((prev) => ({ ...prev, method: "privateKey", value: "" }))
            }
            disabled={state.isImporting}
          >
            <Key className="h-5 w-5" />
            <span className="text-xs">Private Key</span>
          </Button>
          <Button
            variant={state.method === "backup" ? "default" : "outline"}
            className="h-auto py-3 flex-col gap-1"
            onClick={() => {
              setState((prev) => ({ ...prev, method: "backup", value: "" }));
              setSelectedFile(null);
            }}
            disabled={state.isImporting}
          >
            <Upload className="h-5 w-5" />
            <span className="text-xs">Backup File</span>
          </Button>
        </div>

        {/* Wallet name - hidden for backup method */}
        {state.method !== "backup" && (
          <div className="space-y-2">
            <Label htmlFor="import-name">Wallet Name (optional)</Label>
            <Input
              id="import-name"
              placeholder="Imported Wallet"
              value={state.name}
              onChange={(e) =>
                setState((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={state.isImporting}
            />
          </div>
        )}

        {/* Input field */}
        <div className="space-y-2">
          {state.method === "backup" ? (
            <>
              <Label>Backup File</Label>
              <div
                className="flex min-h-[120px] w-full items-center justify-center rounded-md border border-dashed border-input bg-background cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() =>
                  document.getElementById("backup-file-input")?.click()
                }
              >
                <input
                  id="backup-file-input"
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      setState((prev) => ({ ...prev, value: file.name }));
                    }
                  }}
                  disabled={state.isImporting}
                />
                {selectedFile ? (
                  <div className="text-center">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Click to change
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Click to select backup file
                    </p>
                  </div>
                )}
              </div>
              <p className="text-xs text-amber-500">
                ⚠️ Only works on the same device where backup was created
              </p>
            </>
          ) : (
            <>
              <Label htmlFor="import-value">
                {state.method === "mnemonic"
                  ? "Recovery Phrase"
                  : "Private Key"}
              </Label>
              {state.method === "mnemonic" ? (
                <textarea
                  id="import-value"
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                  placeholder="Enter your 12 or 24 word recovery phrase..."
                  value={state.value}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, value: e.target.value }))
                  }
                  disabled={state.isImporting}
                />
              ) : (
                <Input
                  id="import-value"
                  type="password"
                  className="font-mono"
                  placeholder="Enter your base58 encoded private key..."
                  value={state.value}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, value: e.target.value }))
                  }
                  disabled={state.isImporting}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {state.method === "mnemonic"
                  ? "Enter words separated by spaces"
                  : "Base58 encoded 64-byte private key"}
              </p>
            </>
          )}
        </div>

        {state.method === "backup" ? (
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <Upload className="h-6 w-6 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Restore from Backup</p>
                <p className="text-xs text-muted-foreground">
                  The backup file contains your encrypted wallet. You&apos;ll
                  need to authenticate with biometrics after restoring.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <Fingerprint className="h-6 w-6 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Secure Import</p>
                <p className="text-xs text-muted-foreground">
                  Your wallet will be encrypted with biometric authentication.
                  The original phrase/key is never stored.
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          onClick={handleImport}
          disabled={isDisabled}
        >
          {state.isImporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {state.method === "backup" ? "Restoring..." : "Importing..."}
            </>
          ) : state.method === "backup" ? (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Restore from Backup
            </>
          ) : (
            <>
              <Fingerprint className="mr-2 h-4 w-4" />
              Import with Biometrics
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
